/**
 * PASSWORD POLICY SERVICE - Re-export from Shared Core
 * 
 * Mantiene compatibilidad con imports existentes.
 * La implementación real está en @boosting/core.
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
  PasswordPolicyService as CorePasswordPolicyService,
  createPasswordPolicyService,
  type PasswordValidationResult,
  type PasswordPolicyConfig,
} from '@boosting/core';
import { auditLogService } from './audit-log.service';
import { securityAlertsService } from './security-alerts.service';

// Re-export types and core service
export type { PasswordValidationResult, PasswordPolicyConfig };
export { createPasswordPolicyService };

const logger = Logger.getLogger('PasswordPolicy');

// Create core service instance
const coreService = createPasswordPolicyService();

export interface PasswordHistoryEntry {
  id: string;
  userId: string;
  passwordHash: string;
  createdAt: string;
}

/**
 * Extended Password Policy Service for Appmor
 * Adds Firestore-based password history and integrates with audit logging
 */
class AppmorPasswordPolicyService {
  private readonly HISTORY_COLLECTION = 'password_history';

  /**
   * Validar complejidad de contraseña
   */
  validateComplexity(password: string): PasswordValidationResult {
    return coreService.validateComplexity(password);
  }

  /**
   * Verificar que la contraseña no está en el historial
   */
  async checkHistory(userId: string, password: string): Promise<PasswordValidationResult> {
    try {
      const history = await this.getPasswordHistory(userId);

      for (const entry of history) {
        if (await coreService.compareWithHash(password, entry.passwordHash)) {
          return {
            valid: false,
            errors: [`No puedes reutilizar las últimas ${coreService.getConfig().historySize} contraseñas`]
          };
        }
      }

      return { valid: true, errors: [] };
    } catch (error) {
      logger.error('Failed to check password history', error as Error);
      return { valid: true, errors: [] };
    }
  }

  /**
   * Validación completa de contraseña (complejidad + historial)
   */
  async validatePassword(userId: string, password: string): Promise<PasswordValidationResult> {
    const complexityResult = this.validateComplexity(password);
    if (!complexityResult.valid) {
      return complexityResult;
    }

    const historyResult = await this.checkHistory(userId, password);
    if (!historyResult.valid) {
      return historyResult;
    }

    return { valid: true, errors: [] };
  }

  /**
   * Guardar contraseña en historial
   */
  async saveToHistory(userId: string, password: string): Promise<void> {
    try {
      const config = coreService.getConfig();
      const history = await this.getPasswordHistory(userId);

      if (history.length >= config.historySize) {
        const oldest = history[history.length - 1];
        await db.collection(this.HISTORY_COLLECTION).doc(oldest.id).delete();
        logger.debug('Removed oldest password from history', { userId });
      }

      const passwordHash = await coreService.hashPassword(password);

      await db.collection(this.HISTORY_COLLECTION).add({
        userId,
        passwordHash,
        createdAt: new Date().toISOString()
      });

      logger.info('Password saved to history', { userId });
    } catch (error) {
      logger.error('Failed to save password to history', error as Error);
    }
  }

  private async getPasswordHistory(userId: string): Promise<PasswordHistoryEntry[]> {
    const config = coreService.getConfig();
    const snapshot = await db.collection(this.HISTORY_COLLECTION)
      .where('userId', '==', userId)
      .orderBy('createdAt', 'desc')
      .limit(config.historySize)
      .get();

    return snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    })) as PasswordHistoryEntry[];
  }

  /**
   * Verificar si la contraseña del usuario ha expirado
   */
  async isPasswordExpired(userId: string): Promise<boolean> {
    try {
      const userDoc = await db.collection('users').doc(userId).get();
      const userData = userDoc.data();

      if (!userData?.passwordChangedAt) {
        return true;
      }

      return coreService.isExpired(userData.passwordChangedAt);
    } catch (error) {
      logger.error('Failed to check password expiration', error as Error);
      return false;
    }
  }

  /**
   * Registrar cambio de contraseña exitoso
   */
  async onPasswordChanged(userId: string, newPassword: string): Promise<void> {
    await this.saveToHistory(userId, newPassword);

    await db.collection('users').doc(userId).update({
      passwordChangedAt: new Date().toISOString()
    });

    await securityAlertsService.onPasswordChanged(userId);

    await auditLogService.log({
      action: 'PASSWORD_CHANGED',
      userId,
      resourceType: 'user',
      resourceId: userId
    });

    logger.info('Password change recorded', { userId });
  }

  getConfig(): PasswordPolicyConfig {
    return coreService.getConfig();
  }

  setConfig(config: Partial<PasswordPolicyConfig>): void {
    coreService.setConfig(config);
  }

  getRequirementsText(): string[] {
    return coreService.getRequirementsText();
  }
}

export const passwordPolicyService = new AppmorPasswordPolicyService();
